import { useCallback, useState } from 'react'

import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE } from '../domain/evidenceUpload'
import type { CaptureTarget } from '../domain/liveEvidenceCapture'
import { buildCaptureRequest, CAPTURE_MAX_BYTES } from '../domain/liveEvidenceCapture'
import type { JpegImage } from '../lib/imageToJpeg'
import { imageToJpeg } from '../lib/imageToJpeg'
import { invokeCapture } from '../models/liveEvidenceCapture'

/** Why a selected file was refused before anything was sent. */
export type RejectionReason = 'unsupported_type' | 'too_large' | 'unreadable'

export type LiveUploadOutcome =
  /** The selection was refused as a whole: nothing was sent. */
  | { kind: 'rejected'; fileName: string; reason: RejectionReason }
  /** Each file was sent as its own capture; `failures` names the ones that did not land. */
  | { kind: 'sent'; uploaded: number; failures: { fileName: string; message: string }[] }

export interface UseLiveEvidenceUploadResult {
  uploadToStep: (target: CaptureTarget, files: File[]) => Promise<LiveUploadOutcome>
  isUploading: boolean
}

/** The validation the archived page's upload applies, answered as a reason so the page can name it. */
const rejectionOf = (file: File): RejectionReason | null => {
  if (!ALLOWED_MIME_TYPES.includes(file.type)) return 'unsupported_type'
  if (file.size > MAX_FILE_SIZE) return 'too_large'
  return null
}

/**
 * Uploads images to one step of a live inspection through the capture edge
 * functions the mobile app uses (see `buildCaptureRequest`).
 *
 * Every file is validated and re-encoded before the first request, so a file
 * that is the wrong type, too large, or undecodable stops the upload with
 * nothing sent. Past that point each file is its own capture, sent one after
 * another: a failure names its file and leaves the captures that landed in
 * place, as a failed capture in the app leaves the others.
 */
export const useLiveEvidenceUpload = (): UseLiveEvidenceUploadResult => {
  const [isUploading, setIsUploading] = useState(false)

  const uploadToStep = useCallback(async (target: CaptureTarget, files: File[]): Promise<LiveUploadOutcome> => {
    for (const file of files) {
      const reason = rejectionOf(file)
      if (reason !== null) {
        return { kind: 'rejected', fileName: file.name, reason }
      }
    }

    setIsUploading(true)
    try {
      const images: { file: File; jpeg: JpegImage }[] = []
      for (const file of files) {
        let jpeg: JpegImage
        try {
          jpeg = await imageToJpeg(file)
        } catch {
          return { kind: 'rejected', fileName: file.name, reason: 'unreadable' }
        }
        // The capture functions' own limit, on the bytes actually sent.
        if (jpeg.bytes > CAPTURE_MAX_BYTES) {
          return { kind: 'rejected', fileName: file.name, reason: 'too_large' }
        }
        images.push({ file, jpeg })
      }

      const failures: { fileName: string; message: string }[] = []
      let uploaded = 0
      for (const { file, jpeg } of images) {
        try {
          await invokeCapture(buildCaptureRequest(target, jpeg.base64))
          uploaded += 1
        } catch (error) {
          failures.push({ fileName: file.name, message: error instanceof Error ? error.message : String(error) })
        }
      }

      return { kind: 'sent', uploaded, failures }
    } finally {
      setIsUploading(false)
    }
  }, [])

  return { uploadToStep, isUploading }
}
